import React from 'react';
import { Clock, X } from 'lucide-react';

export type BeforeActionType = 'clock_out' | 'break';

interface ActiveJobTimerBeforeActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Whether they're trying to clock out or take/end a break */
  actionType: BeforeActionType;
  activeTimerCount: number;
  onStopAndProceed: () => Promise<void>;
  loading?: boolean;
}

function getCopy(actionType: BeforeActionType, count: number) {
  const timerText = count === 1 ? 'job timer' : 'job timers';
  if (actionType === 'clock_out') {
    return {
      title: 'Active job timer' + (count > 1 ? 's' : ''),
      body: `You have ${count} active ${timerText}. Stop ${count > 1 ? 'them' : 'it'} to clock out?`,
      button: 'Stop timer' + (count > 1 ? 's' : '') + ' and clock out',
    };
  }
  return {
    title: 'Active job timer' + (count > 1 ? 's' : ''),
    body: `You have ${count} active ${timerText}. Stop ${count > 1 ? 'them' : 'it'} to take a break?`,
    button: 'Stop timer' + (count > 1 ? 's' : '') + ' and take break',
  };
}

export default function ActiveJobTimerBeforeActionModal({
  isOpen,
  onClose,
  actionType,
  activeTimerCount,
  onStopAndProceed,
  loading = false,
}: ActiveJobTimerBeforeActionModalProps) {
  if (!isOpen) return null;

  const { title, body, button } = getCopy(actionType, activeTimerCount);

  return (
    <div className="fixed inset-0 z-[100] min-h-screen flex items-center justify-center bg-black/50 p-4" onClick={() => !loading && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden mx-auto my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="px-5 pb-5 text-sm text-gray-500">{body}</p>
        <div className="flex gap-2 px-5 pb-5">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onStopAndProceed()}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Stopping…
              </>
            ) : (
              button
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
